import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import DropDown from "../forms/dropdown/DropDown";
import { sortedCountriesByLanguage } from "../../helpers/options/countries";
import i18next from "i18next";
import { useForm } from "react-hook-form";
import { UserPaymentAddress } from "../../api/models/UserModel";

type HyperpayCheckoutInfoProps = {
  minPaymentAmount: number;
  maxPaymentAmount: number;
  initialAddress: UserPaymentAddress;
  onConfirm: (info: HyperpayFinalizedCheckoutInfoType) => void;
};

export type HyperpayFinalizedCheckoutInfoType = {
  country: string;
  city: string;
  state: string;
  street: string;
  zip: string;
  amount: number;
  first_name: string;
  last_name: string;
};

type HyperpayCheckoutInfoType = {
  country?: string;
  city?: string;
  state?: string;
  street?: string;
  zip?: string;
  amount?: number;
  first_name?: string;
  last_name?: string;
};

export default function HyperpayCheckoutInfo(props: HyperpayCheckoutInfoProps) {
  const { minPaymentAmount, maxPaymentAmount, initialAddress, onConfirm } = props;
  const { t } = useTranslation("", {
    keyPrefix: "page.candidate.dashboard.payment",
  });

  const initialValues: HyperpayCheckoutInfoType = initialAddress;
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({ defaultValues: initialValues });

  // Watch input changes to handle autofill
  useEffect(() => {
    const fields = [
      "country",
      "city",
      "state",
      "street",
      "zip",
      "amount",
      "first_name",
      "last_name",
    ];
    fields.forEach((field:any) => {
      const element:any = document.querySelector(`input[name=${field}]`);
      if (element) {
        element.addEventListener("input", () => {
          setValue(field, element.value);
        });
      }
    });
  }, [setValue]);

  const countriesOptions = useMemo(() => {
    return sortedCountriesByLanguage(i18next.language).map((country) => {
      return {
        key:
          i18next.language === "ar"
            ? country.country_name_ar
            : country.country_name,
        value: country.country_name,
      };
    });
  }, [i18next.language]);

  const onSubmit = (data: HyperpayCheckoutInfoType) => {
    console.log("data", data);
    onConfirm(data as HyperpayFinalizedCheckoutInfoType);
  };

  return (
    <div className="w-full h-full">
      <form
        id="subscription-form"
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
      >
        <h3 className="text-sm font-medium text-gray-700">
          {t("paymentAddress")}
        </h3>
        <div className="flex flex-col items-start w-full gap-4 lg:w-5/6">
          <DropDown
            id="country"
            name="country"
            schema={{ required: true }}
            register={register}
            setValue={(value: any) =>
              setValue("country", value, { shouldValidate: true })
            }
            defaultValue={`${getValues("country")}`}
            label={t("country")}
            placeholder={t("country")}
            error={!!errors.country}
            options={countriesOptions}
          />
        </div>
        <div className="flex flex-col items-start gap-4">
          <label htmlFor="city" className="text-sm font-normal text-gray-800 ">
            {`${t("city")} *`}
          </label>
          <input
            {...register("city", { required: true })}
            type="text"
            placeholder="New York, Sydney, Cairo ..."
            name="city"
            className="w-full border-gray-300 rounded focus:ring-brandblue-500 lg:w-5/6 text-brandblue-600"
          />
        </div>
        <div className="flex flex-col items-start gap-4">
          <label htmlFor="state" className="text-sm font-normal text-gray-800 ">
            {`${t("state")} *`}
          </label>
          <input
            {...register("state", { required: true })}
            type="text"
            placeholder="New York, Sydney, Cairo ..."
            name="state"
            className="w-full border-gray-300 rounded focus:ring-brandblue-500 lg:w-5/6 text-brandblue-600"
          />
        </div>
        <div className="flex flex-col items-start gap-4">
          <label
            htmlFor="street"
            className="text-sm font-normal text-gray-800 "
          >
            {`${t("street")} *`}
          </label>
          <input
            {...register("street", { required: true })}
            type="text"
            name="street"
            placeholder="5th Avenue"
            className="w-full border-gray-300 rounded focus:ring-brandblue-500 lg:w-5/6 text-brandblue-600"
          />
        </div>
        <div className="flex flex-col items-start gap-4">
          <label htmlFor="zip" className="text-sm font-normal text-gray-800 ">
            {`${t("zipcode")} *`}
          </label>
          <input
            {...register("zip", { required: true })}
            type="text"
            name="zip"
            placeholder="11239"
            className="w-full border-gray-300 rounded focus:ring-brandblue-500 lg:w-5/6 text-brandblue-600"
          />
        </div>
        <div className="flex flex-col items-start gap-4">
          <label
            htmlFor="first_name"
            className="text-sm font-normal text-gray-800 "
          >
            {`${t("first_name")} *`}
          </label>
          <input
            {...register("first_name", { required: true })}
            type="text"
            name="first_name"
            placeholder="John"
            className="w-full border-gray-300 rounded focus:ring-brandblue-500 lg:w-5/6 text-brandblue-600"
          />
        </div>
        <div className="flex flex-col items-start gap-4">
          <label
            htmlFor="last_name"
            className="text-sm font-normal text-gray-800 "
          >
            {`${t("last_name")} *`}
          </label>
          <input
            {...register("last_name", { required: true })}
            type="text"
            name="last_name"
            placeholder="Doe"
            className="w-full border-gray-300 rounded focus:ring-brandblue-500 lg:w-5/6 text-brandblue-600"
          />
        </div>

        <h3 className="text-sm font-medium text-gray-700">
          {t("paymentCost")}
        </h3>
        <div className="flex flex-col items-start gap-4">
          <input
            {...register("amount", {
              required: true,
              min: minPaymentAmount,
              max: maxPaymentAmount
            })}
            type="number"
            name="amount"
            placeholder={`$${minPaymentAmount}`}
            className="w-full border-gray-300 rounded focus:ring-brandblue-500 lg:w-5/6 text-brandblue-600"
          />
          <label
            htmlFor="subscription-cost"
            className="text-sm font-normal text-gray-500 "
          >
            {t("attentionPayment", { cost: minPaymentAmount })}
          </label>
          <label
            htmlFor="subscription-cost"
            className="text-sm font-normal text-gray-500 "
          >
            {t("attentionMaxPayment", { cost: maxPaymentAmount })}
          </label>
        </div>
        <div className="flex items-center justify-start gap-4">
          <button
            className="flex justify-center w-full px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm sm:w-auto bg-brandblue-600 hover:bg-brandblue-700 focus:ring-brandblue-500 focus:outline-none focus:ring-2 focus:ring-offset-2"
            type="submit"
          >
            <span id="button-text">{t("confirmPayment")}</span>
          </button>
        </div>
      </form>
    </div>
  );
}
